/**
 * meters/helpers.ts — Shared pure helpers for meter-model parsing
 * ================================================================
 * No side effects. Used by the normalizer and by each model's finalizeRaw().
 */

import type { RawMeterRow } from '@/types';

// ─── Header normalisation ─────────────────────────────────────────────────────

/**
 * Normalise a sheet header for tolerant matching: lower-case and strip every
 * non-alphanumeric character. So "V_RY", "V RY", "v-ry" and "VRY" all collapse
 * to "vry". This lets a model's fieldMap use the human header verbatim while
 * still matching minor formatting differences in the live sheet.
 */
export function normalizeHeader(h: string): string {
  return String(h).toLowerCase().replace(/[^a-z0-9]/g, '');
}

// ─── Numeric coercion ─────────────────────────────────────────────────────────

export const toNum = (v: unknown, def = 0): number => {
  if (v === null || v === undefined || v === '') return def;
  // GViz CSV often returns large registers with thousand-separator commas
  // (e.g. "1,234,567"). Number("1,234,567") is NaN → would zero the value,
  // so strip commas first. (Also strips stray spaces / a leading apostrophe.)
  const clean = typeof v === 'string'
    ? v.replace(/,/g, '').replace(/^'/, '').trim()
    : v;
  const n = Number(clean);
  return Number.isFinite(n) ? n : def;
};

export const toStr = (v: unknown, def = ''): string =>
  v === null || v === undefined ? def : String(v).trim();

// ─── Empty canonical row ──────────────────────────────────────────────────────

/**
 * A fully zero-initialised RawMeterRow. The normalizer overwrites whatever the
 * meter reports; every other field stays 0/'' so downstream code never hits
 * `undefined`. Timestamp is set by the caller.
 */
export function emptyRawRow(ts: Date): RawMeterRow {
  return {
    timestamp: ts,
    wattsTotal: 0, wattsR: 0, wattsY: 0, wattsB: 0,
    varTotal: 0, varR: 0, varY: 0, varB: 0,
    truePFAvg: 0, truePFR: 0, truePFY: 0, truePFB: 0,
    vaTotal: 0, vaR: 0, vaY: 0, vaB: 0,
    vllAvg: 0, vRY: 0, vYB: 0, vBR: 0,
    vlnAvg: 0, vR: 0, vY: 0, vB: 0,
    iTotal: 0, iR: 0, iY: 0, iB: 0,
    frequencyHz: 0,
    whReceived: 0, vahReceived: 0, varhIndReceived: 0, varhCapReceived: 0,
    whDelivered: 0, vahDelivered: 0, varhIndDelivered: 0, varhCapDelivered: 0,
    pfAvgReceived: 0, ampsHourReceived: 0, pfAvgDelivered: 0, ampsHourDelivered: 0,
    neutralI: 0,
    vRHarmonic: 0, vYHarmonic: 0, vBHarmonic: 0,
    iRHarmonic: 0, iYHarmonic: 0, iBHarmonic: 0,
    risingDemand: 0, maxDemandMeter: 0, risingDemandDelivered: 0, maxDemandDelivered: 0,
    displacementPFR: 0, displacementPFB: 0,
    rpm: 0, loadHoursReceived: 0, loadHoursDelivered: 0,
    numInterruptions: 0, mdOccurrenceTime: '',
    vRPhaseAngle: 0, vYPhaseAngle: 0, vBPhaseAngle: 0,
    iRPhaseAngle: 0, iYPhaseAngle: 0, iBPhaseAngle: 0,
    vUnbalR: 0, vUnbalY: 0, vUnbalB: 0,
    iUnbalR: 0, iUnbalY: 0, iUnbalB: 0,
    analogInput1: 0, analogInput2: 0,
    vllMax: 0, vllMin: 0, vlnMax: 0, vlnMin: 0,
    ampsMax: 0, ampsMin: 0, frequencyMax: 0, frequencyMin: 0,
    wattsMax: 0, wattsMin: 0, varMax: 0, varMin: 0,
    vaMax: 0, vaMin: 0, pfMax: 0, pfMin: 0,
    vRThdPct: 0, vYThdPct: 0, vBThdPct: 0,
    iRThdPct: 0, iYThdPct: 0, iBThdPct: 0,
    kFactorVR: 0, kFactorVY: 0, kFactorVB: 0,
    kFactorIR: 0, kFactorIY: 0, kFactorIB: 0,
    // Backward-compat aliases (re-synced by applyAliases)
    currentTotal: 0, currentR: 0, currentY: 0, currentB: 0,
    pfAvgRaw: 0, pfRRaw: 0, pfYRaw: 0, pfBRaw: 0,
    energyWh: 0, energyVAh: 0,
  };
}

// ─── Aliases ──────────────────────────────────────────────────────────────────

/**
 * Sync the legacy field aliases that existing components/engines still read.
 * Called at the end of every model's finalizeRaw() so the whole app — old and
 * new code paths — sees consistent values regardless of meter model.
 */
export function applyAliases(r: RawMeterRow): RawMeterRow {
  if (r.iTotal && !r.currentTotal) r.currentTotal = r.iTotal;
  else if (r.currentTotal && !r.iTotal) r.iTotal = r.currentTotal;

  if (r.iR && !r.currentR) r.currentR = r.iR;
  else if (r.currentR && !r.iR) r.iR = r.currentR;

  if (r.iY && !r.currentY) r.currentY = r.iY;
  else if (r.currentY && !r.iY) r.iY = r.currentY;

  if (r.iB && !r.currentB) r.currentB = r.iB;
  else if (r.currentB && !r.iB) r.iB = r.currentB;

  if (r.truePFAvg && !r.pfAvgRaw) r.pfAvgRaw = r.truePFAvg;
  else if (r.pfAvgRaw && !r.truePFAvg) r.truePFAvg = r.pfAvgRaw;

  if (r.truePFR && !r.pfRRaw) r.pfRRaw = r.truePFR;
  else if (r.pfRRaw && !r.truePFR) r.truePFR = r.truePFR;

  if (r.truePFY && !r.pfYRaw) r.pfYRaw = r.truePFY;
  else if (r.pfYRaw && !r.truePFY) r.truePFY = r.pfYRaw;

  if (r.truePFB && !r.pfBRaw) r.pfBRaw = r.truePFB;
  else if (r.pfBRaw && !r.truePFB) r.truePFB = r.pfBRaw;

  if (r.whReceived && !r.energyWh) r.energyWh = r.whReceived;
  else if (r.energyWh && !r.whReceived) r.whReceived = r.energyWh;

  if (r.vahReceived && !r.energyVAh) r.energyVAh = r.vahReceived;
  else if (r.energyVAh && !r.vahReceived) r.vahReceived = r.energyVAh;

  return r;
}

// ─── Reactive power derivation ────────────────────────────────────────────────

/** Q = sqrt(max(0, S² − P²)). Used to back-fill VAR when a meter omits it. */
export function reactiveFromApparent(va: number, watts: number): number {
  const q2 = va * va - watts * watts;
  return q2 > 0 ? Math.sqrt(q2) : 0;
}

// ─── 32-bit register decode repair ────────────────────────────────────────────

/** One 16-bit word. A swapped word pair leaves a small count multiplied by this. */
const WORD = 65536;

/**
 * Largest plausible cumulative load-hours reading (~114 years of runtime).
 * Anything above this is a mis-decoded register, not a measurement.
 */
const LOAD_HOURS_CEILING = 1_000_000;

/**
 * Repair known-bad 32-bit register decodes from the ESP/Pi Modbus poller.
 *
 * Verified against Firebase_Data_Export.csv (30,831 rows, all 5 tenants):
 *
 *   • numInterruptions — EVERY non-zero value is an exact multiple of 65536,
 *     taking only the values 1,2,3,4,5,7,9 × 65536. The 32-bit word pair is
 *     transmitted high/low swapped, so a count of 9 arrives as 9 << 16.
 *
 *   • loadHours{Received,Delivered} — 49% of non-zero values exceed 2^31
 *     (unsigned-wrapped negatives) and the series is not monotonic, which a
 *     cumulative hour counter must be. The register is unusable, so it is
 *     zeroed rather than passed on: a future feature must not silently trust it.
 *
 * The real fix is in the poller firmware. Both guards are no-ops once a
 * correctly-decoded value arrives, so this can stay in place across that fix.
 *
 * ponytail: word-swap undo assumes the true count is < 65536 — safe for
 * interruption counts, revisit if a register ever legitimately exceeds that.
 */
export function sanitizeRawRow(r: RawMeterRow): RawMeterRow {
  const n = r.numInterruptions;
  if (Number.isFinite(n) && n > 0 && n % WORD === 0) {
    r.numInterruptions = n / WORD;
  }

  if (!(r.loadHoursReceived >= 0 && r.loadHoursReceived < LOAD_HOURS_CEILING)) {
    r.loadHoursReceived = 0;
  }
  if (!(r.loadHoursDelivered >= 0 && r.loadHoursDelivered < LOAD_HOURS_CEILING)) {
    r.loadHoursDelivered = 0;
  }

  return r;
}
